/**
 * Modal para marcar un arqueo como controlado o deshacer el control.
 *
 * Lo comparten el tablero (ControlArqueosPanel) y el detalle del arqueo
 * (CajaDetalle): el dueño entra al detalle para revisar el cierre y lo aprueba
 * desde ahí, sin volver a la lista.
 */

import React, { useState } from 'react';
import { Check } from 'lucide-react';
import toast from 'react-hot-toast';

import { supabase } from '../../supabase';
import { buildDeshacerControlPayload, buildMarcarControlPayload } from '../../utils/arqueoReview';
import { formatOperationDateTime } from '../../utils/localDateTime';

const money = (value) => {
  if (value === null || value === undefined || value === '') return '—';
  const numero = Number(value);
  if (!Number.isFinite(numero)) return '—';
  return numero.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

/**
 * Persiste el control. La condición sobre controlado_at hace el update
 * idempotente: dos admins marcando el mismo arqueo a la vez, o un doble click,
 * no se pisan.
 *
 * @returns {Promise<boolean>} true si se aplicó el cambio.
 */
async function registrarControlArqueo({ profile, turno, accion, nota = '' }) {
  const esMarcar = accion === 'marcar';
  const payload = esMarcar
    ? buildMarcarControlPayload({ profile, turno, observacion: nota })
    : buildDeshacerControlPayload({ profile, turno, motivo: nota });

  let query = supabase.from('turnos_caja').update(payload).eq('id', turno.id);
  query = esMarcar ? query.is('controlado_at', null) : query.not('controlado_at', 'is', null);

  const { data, error } = await query.select('id');
  if (error) throw error;

  if (!data || data.length === 0) {
    toast.error(
      esMarcar
        ? 'Ese arqueo ya lo controló otra persona. Se actualizó la información.'
        : 'Ese arqueo ya no estaba controlado.',
    );
    return false;
  }

  toast.success(esMarcar ? 'Arqueo marcado como controlado' : 'Se deshizo el control');
  return true;
}

function ControlArqueoModal({ turno, accion, profile, sitioNombre = '', onClose, onDone }) {
  const [nota, setNota] = useState('');
  const [guardando, setGuardando] = useState(false);

  if (!turno) return null;
  const esMarcar = accion === 'marcar';

  const confirmar = async () => {
    setGuardando(true);
    try {
      const aplicado = await registrarControlArqueo({ profile, turno, accion, nota });
      await onDone?.({ aplicado, accion });
    } catch (error) {
      console.error('No se pudo registrar el control del arqueo:', error);
      // 42501 es el trigger de la base rechazando a quien no es administrador.
      const mensaje =
        error?.code === '42501' || /administrador/i.test(error?.message || '')
          ? 'Solo un administrador de la empresa puede controlar arqueos.'
          : 'No se pudo registrar el control. Reintentá.';
      toast.error(mensaje);
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#0B2A36]/40 p-4">
      <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-5 shadow-xl">
        <p className="text-[11px] font-black uppercase tracking-tight text-[#0B2A36]">
          {esMarcar ? 'Marcar arqueo como controlado' : 'Deshacer el control'}
        </p>
        <p className="mt-1 text-[10px] font-bold uppercase tracking-widest text-slate-400">
          {sitioNombre ? `${sitioNombre} · ` : ''}
          {formatOperationDateTime(turno.fecha_cierre)} · diferencia $ {money(turno.diferencia)}
        </p>

        <textarea
          value={nota}
          onChange={(e) => setNota(e.target.value)}
          rows={3}
          maxLength={500}
          placeholder={
            esMarcar
              ? 'Observación (opcional): por qué se acepta la diferencia, qué se revisó…'
              : 'Motivo (opcional)'
          }
          className="mt-3 w-full rounded-xl border border-slate-100 bg-slate-50 p-3 text-[11px] font-bold text-slate-600 outline-none focus:border-[#51A7FD]"
        />

        {!esMarcar && (
          <p className="mt-2 text-[9px] font-bold uppercase tracking-widest text-slate-400">
            El arqueo vuelve a pendiente. Queda registrado quién lo había controlado.
          </p>
        )}

        <div className="mt-4 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={guardando}
            className="rounded-xl px-4 py-2.5 text-[9px] font-black uppercase tracking-widest text-slate-400 hover:text-slate-600"
          >
            Cancelar
          </button>
          <button
            type="button"
            disabled={guardando}
            onClick={confirmar}
            className="inline-flex items-center gap-2 rounded-xl bg-[#0B2A36] px-4 py-2.5 text-[9px] font-black uppercase tracking-widest text-white shadow-sm transition-all hover:brightness-110 disabled:opacity-50"
          >
            <Check size={12} />
            {esMarcar ? 'Confirmar control' : 'Deshacer'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ControlArqueoModal;
